import { useMemo } from "react";
import currency from "currency.js";
import { SideResponse, Stop } from "../../../codegen-api";
import { FormValidatorKeysEnum } from "../../../enums/form";
import { passesMarkCheck } from "../../../utils/markGuards";
import { AssetResponse } from "../../../utils/asset";
import { floorSizeForDisplay, roundUpToPrecision } from "../../../utils/format";
import { floorToDecimalsString } from "../../../utils/decimalString";
import { USDC_TERMS_PRECISION } from "./PerpsTradeForm.helpers";
import { IPositionInfo } from "../../../interfaces/TradeInfo";
import { usePerpsTradeFormCreateOrder } from "./usePerpsTradeFormCreateOrder";

/**
 * The first price that actually EXISTS, for sizing the minimum order value:
 * the touch of the side being traded, then the order's own limit price, then
 * the mark.
 *
 * The caller used to branch on the order book OBJECT being present rather than
 * on the relevant side having depth. A one-sided book -- the normal state on a
 * thin market -- gave `undefined`, `Number(undefined || 0)` made it 0, and the
 * minimum became $0.00, so the client-side size check silently stopped firing
 * and dust orders reached the chain to be rejected there. The limit-price
 * branch was unreachable for the same reason: its guard was true whenever the
 * book object existed, depth or not.
 *
 * `undefined` means there is genuinely no price to measure against, which is a
 * real state and not the same as zero.
 */
export const referencePriceForMinimum = (
  candidates: Array<string | number | null | undefined>
): number | undefined =>
  candidates
    .map((candidate) => Number(candidate))
    .find((candidate) => Number.isFinite(candidate) && candidate > 0);

export function usePerpsTradeFormValidators(
  ctx: ReturnType<typeof usePerpsTradeFormCreateOrder>
) {
  const {
    commonFormTranslations,
    accountData,
    buyingPower,
    notEnoughBalanceError,
    formTranslations,
    perpInstrument,
    currentPosition,
    contractPriceStep,
    orderbookData,
    orderDirection,
    price,
    stopType,
    triggerPrice,
    markPrice,
    tradeUseUSDCTerms,
    amount,
  } = ctx;

  const positionInfo = useMemo<IPositionInfo[]>(() => {
    const posInfo: IPositionInfo[] = [
      {
        title: commonFormTranslations("margin_balance"),
        // Balances a trader may type back in are floored, never rounded up:
        // $99.996 of buying power shown as "$100.00" invited a rejected order.
        value: accountData?.available_balance
          ? currency(floorToDecimalsString(accountData.available_balance, 2)).format()
          : "---",
        side: undefined,
      },
      {
        title: commonFormTranslations("buying_power"),
        value: buyingPower
          ? currency(floorToDecimalsString(buyingPower, 2)).format()
          : "---",
        side: undefined,
        warningOrError: notEnoughBalanceError ? "error" : undefined,
        showErrorIcon: true,
      },
      {
        title: `${formTranslations("position")} (${
          perpInstrument?.underlying_asset as AssetResponse
        })`,
        value: currentPosition
          ? floorSizeForDisplay(
              currentPosition.amount,
              contractPriceStep.amount_step,
              contractPriceStep.amount_precision,
              "-"
            )
          : "---",
        side: currentPosition?.side,
      },
    ];
    return posInfo;
  }, [
    commonFormTranslations,
    formTranslations,
    contractPriceStep.amount_precision,
    contractPriceStep.amount_step,
    accountData?.available_balance,
    buyingPower,
    notEnoughBalanceError,
    perpInstrument?.underlying_asset,
    currentPosition,
  ]);

  const minimumUSDCValueForValidContractSize = useMemo(() => {
    // The smallest order is `min_order_amount` (the chain's floor), not one
    // rounding step: BTC spot has step 1e-8 but a 0.00001 floor, so the step
    // gave a $0.0006 minimum and let $0.50 orders through to fail at submit.
    // The result is USDC, so it is rounded UP to cents, not to the size precision.
    const { min_order_amount } = contractPriceStep;
    // Pick the first reference price that actually EXISTS.
    //
    // This used to branch on `orderbookData` being present rather than on the
    // relevant side having depth. A one-sided book -- the normal state on a thin
    // market -- made `?.[0]?.[0]` undefined, `Number(undefined || 0)` zero, and
    // the minimum $0.00, so `minimum_size_error` below silently stopped firing
    // and a dust order sailed past the client only to revert at submit. The
    // limit-price branch could never be reached either: it sat behind a check
    // that is true whenever the book object exists at all, book depth or not.
    const touch = (
      orderDirection === SideResponse.Buy
        ? orderbookData?.asks
        : orderbookData?.bids
    )?.[0]?.[0];
    const reference = referencePriceForMinimum([touch, price, markPrice]);
    // Still nothing to measure against: 0 disables the check, which is the
    // honest outcome -- it is the chain's floor that matters, and inventing a
    // minimum from no price would reject valid orders.
    if (reference === undefined) return 0;
    return roundUpToPrecision(
      min_order_amount * reference,
      USDC_TERMS_PRECISION
    );
  }, [contractPriceStep, markPrice, orderDirection, orderbookData, price]);

  const limitPriceValidateFn = useMemo(() => {
    if (stopType === Stop.StopLoss) {
      // If stop limit and buy, limit price >= trigger price
      if (orderDirection === SideResponse.Buy) {
        return {
          [FormValidatorKeysEnum.limitPriceAboveTriggerPrice]: (v: string) =>
            parseFloat(v) >= Number(triggerPrice),
          [FormValidatorKeysEnum.limitPriceBelowTriggerPrice]: () => true,
        };
      }
      return {
        [FormValidatorKeysEnum.limitPriceAboveTriggerPrice]: () => true,
        [FormValidatorKeysEnum.limitPriceBelowTriggerPrice]: (v: string) =>
          parseFloat(v) <= Number(triggerPrice),
      };
    }
    if (stopType === Stop.TakeProfit) {
      if (orderDirection === SideResponse.Buy) {
        // If take profit and buy, limit price >= trigger price
        return {
          [FormValidatorKeysEnum.limitPriceAboveTriggerPrice]: () => true,
          [FormValidatorKeysEnum.limitPriceBelowTriggerPrice]: (v: string) =>
            parseFloat(v) <= Number(triggerPrice),
        };
      }
      return {
        [FormValidatorKeysEnum.limitPriceAboveTriggerPrice]: (v: string) =>
          parseFloat(v) >= Number(triggerPrice),
        [FormValidatorKeysEnum.limitPriceBelowTriggerPrice]: () => true,
      };
    }

    // if not stops, dont do anything
    return {
      [FormValidatorKeysEnum.limitPriceAboveTriggerPrice]: () => true,
      [FormValidatorKeysEnum.limitPriceBelowTriggerPrice]: () => true,
    };
  }, [orderDirection, stopType, triggerPrice]);

  const triggerPriceValidateFn = useMemo(() => {
    if (!stopType) {
      return {
        [FormValidatorKeysEnum.triggerPriceBelowMark]: () => true,
        [FormValidatorKeysEnum.triggerPriceAboveMark]: () => true,
      };
    }

    // If BUY STOP LOSS or SELL TAKE PROFIT, trigger price must be higher than mark
    if (
      (orderDirection === SideResponse.Buy && stopType === Stop.StopLoss) ||
      (orderDirection === SideResponse.Sell && stopType === Stop.TakeProfit)
    ) {
      return {
        [FormValidatorKeysEnum.triggerPriceBelowMark]: () => true,
        // Unknown mark: the check cannot run (never compared with 0/NaN).
        [FormValidatorKeysEnum.triggerPriceAboveMark]: (v: string) =>
          passesMarkCheck(v, markPrice, "atOrAbove"),
      };
    }
    // If SELL STOP LOSS or BUY TAKE PROFIT, trigger price must be lower than mark
    return {
      [FormValidatorKeysEnum.triggerPriceBelowMark]: (v: string) =>
        passesMarkCheck(v, markPrice, "atOrBelow"),
      [FormValidatorKeysEnum.triggerPriceAboveMark]: () => true,
    };
  }, [markPrice, orderDirection, stopType]);

  const customError = useMemo(
    () =>
      tradeUseUSDCTerms &&
      amount &&
      Number(amount) < minimumUSDCValueForValidContractSize
        ? formTranslations("minimum_size_error", {
            size: minimumUSDCValueForValidContractSize,
            asset: "USDC",
          })
        : undefined,
    [
      amount,
      formTranslations,
      minimumUSDCValueForValidContractSize,
      tradeUseUSDCTerms,
    ]
  );

  return {
    ...ctx,
    positionInfo,
    minimumUSDCValueForValidContractSize,
    limitPriceValidateFn,
    triggerPriceValidateFn,
    customError,
  };
}
